import { PRODUCT_TYPES } from "@shared/constants/paidFeaturesHierarchy";
import AIKnowledgeBaseIconActive from "@shared/svg/ai-knowledgebase-active.svg?react";
import AIKnowledgeBaseIcon from "@shared/svg/ai-knowledgebase.svg?react";
import AiNotetakerIconActive from "@shared/svg/ai-notetaker-active.svg?react";
import AiNotetakerIcon from "@shared/svg/ai-notetaker.svg?react";
import AISearchIconActive from "@shared/svg/ai-search-active.svg?react";
import AISearchIcon from "@shared/svg/ai-search.svg?react";
import ArchiveWorkOrderIcon from "@shared/svg/archive-work-order.svg?react";
import HelpIcon from "@shared/svg/help.svg?react";
import AiAssistantsIconActive from "@shared/svg/navigation/ai-assistants-active.svg?react";
import AiAssistantsIcon from "@shared/svg/navigation/ai-assistants.svg?react";
import AiChatsIconActive from "@shared/svg/navigation/ai-chats-active.svg?react";
import AiChatsIcon from "@shared/svg/navigation/ai-chats.svg?react";
import AssetsActiveIcon from "@shared/svg/navigation/assets-active.svg?react";
import AssetsIcon from "@shared/svg/navigation/assets.svg?react";
import CompanyIconActive from "@shared/svg/navigation/companies-active.svg?react";
import CompanyIcon from "@shared/svg/navigation/companies.svg?react";
import ContactsIconActive from "@shared/svg/navigation/contacts-active.svg?react";
import ContactsIcon from "@shared/svg/navigation/contacts.svg?react";
import EmailIconActive from "@shared/svg/navigation/emails-active.svg?react";
import EmailIcon from "@shared/svg/navigation/emails.svg?react";
import HelpActiveIcon from "@shared/svg/navigation/help-center-active.svg?react";
import InsightsIconActive from "@shared/svg/navigation/insights-active.svg?react";
import InsightsIcon from "@shared/svg/navigation/insights.svg?react";
import MyWorkOrdersActiveIcon from "@shared/svg/navigation/my-work-orders-active.svg?react";
import MyWorkOrdersIcon from "@shared/svg/navigation/my-work-orders.svg?react";
import PartsIconActive from "@shared/svg/navigation/parts-active.svg?react";
import PartsIcon from "@shared/svg/navigation/parts.svg?react";
import PlannedEventsActiveIcon from "@shared/svg/navigation/planned-events-active.svg?react";
import PlannedEventsIcon from "@shared/svg/navigation/planned-events.svg?react";
import PortalsActiveIcon from "@shared/svg/navigation/portals-active.svg?react";
import PortalsIcon from "@shared/svg/navigation/portals.svg?react";
import ProcedureIconActive from "@shared/svg/navigation/procedures-active.svg?react";
import ProcedureIcon from "@shared/svg/navigation/procedures.svg?react";
import ProductsActiveIcon from "@shared/svg/navigation/products-active.svg?react";
import ProductsIcon from "@shared/svg/navigation/products.svg?react";
import SchedulerActiveIcon from "@shared/svg/navigation/scheduler-active.svg?react";
import SchedulerIcon from "@shared/svg/navigation/scheduler.svg?react";
import WorkOrdersIconActive from "@shared/svg/navigation/work-orders-active.svg?react";
import WorkOrdersIcon from "@shared/svg/navigation/work-orders.svg?react";
import QuotesInvoicesNavIcon from "@shared/svg/navigation/quotes-invoices.svg?react";
import QuotesInvoicesNavIconActive from "@shared/svg/navigation/quotes-invoices-active.svg?react";
import InvoicesNavIcon from "@shared/svg/navigation/invoices.svg?react";
import InvoicesNavIconActive from "@shared/svg/navigation/invoices-active.svg?react";
import RequestFilledActiveIcon from "@shared/svg/request-filled-active.svg?react";
import RequestIcon from "@shared/svg/request.svg?react";
import SettingsActiveIcon from "@shared/svg/settings-filled.svg?react";
import SettingsIcon from "@shared/svg/settings.svg?react";
import type { UserType } from "@shared/types/user";
import { useIntl } from "react-intl";
import { useLocation, useMatch } from "react-router-dom";

import PAID_FEATURES from "$/settings/paid-features.json";
import { isHiddenInNavigation } from "~/constants/routePermissions";
import { useGlobalNavStore } from "~/stores/globalNavStore";
import { getEnums } from "~/utils";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import useNomenclature from "~/hooks/useNomenclature";

const paidFeatures = getEnums(PAID_FEATURES, "reference");

export type RouteConfig = {
  to?: string;
  icon: JSX.Element;
  activeIcon: JSX.Element;
  disableIf?: (user: UserType, appFeatures: string[]) => boolean;
  label: string;
  notificationKey?: string;
  className?: string;
  isActive?: boolean;
};

export function useRoutes(
  basePath: string = "/app",
  hasIntercomAppId: boolean = false,
) {
  const { messages } = useIntl();
  const { basePath: companyBasePath } = useNomenclature();
  const appConfig = useGetAppConfigValue();
  const { state } = useLocation();
  const isOnWODetail = !!useMatch(`${basePath}/work/work-orders/:id`);
  // Synchronous: navigation state set by WorkOrderListCard when clicking from archived list
  const fromArchivedNav = isOnWODetail && !!state?.fromArchived;
  // Async fallback: store updated by WorkOrderDetails once data loads (direct URL access)
  const isViewingArchivedWorkOrder = useGlobalNavStore(
    (s) => s.isViewingArchivedWorkOrder,
  );
  const isArchivedWO = fromArchivedNav || isViewingArchivedWorkOrder;

  const contentRouteSections: RouteConfig[][] = [
    // Asset Hub Routes
    [
      {
        to: `${basePath}/asset-hub/my-assets`,
        icon: <AssetsIcon />,
        activeIcon: <AssetsActiveIcon />,
        label: (messages?.["sidenav"]?.["assets"] as string) || "Assets",
      },
      {
        to: `${basePath}/asset-hub/my-products`,
        icon: <ProductsIcon />,
        activeIcon: <ProductsActiveIcon />,
        label: (messages?.["sidenav"]?.["products"] as string) || "Products",
      },
      {
        to: `${basePath}/asset-hub/inventory`,
        icon: <PartsIcon />,
        activeIcon: <PartsIconActive />,
        label: messages?.menus?.["asset-hub"]?.["SUB_MENUS"]?.[
          "INVENTORY"
        ] as string,
      },
    ],
    // Work Routes
    [
      {
        to: `${basePath}/work/tickets`,
        icon: <RequestIcon />,
        activeIcon: <RequestFilledActiveIcon />,
        label:
          (messages?.menus?.["cmms"]?.["SUB_MENUS"]?.[
            "SUPPORT_TICKETS"
          ] as string) ?? "Tickets",
        notificationKey: "supportTickets",
      },
      {
        to: `${basePath}/work/work-orders`,
        icon: <WorkOrdersIcon />,
        activeIcon: <WorkOrdersIconActive />,
        label: messages?.menus?.["cmms"]?.["SUB_MENUS"]?.[
          "WORK_ORDERS"
        ] as string,
        notificationKey: "openTickets",
        isActive: isArchivedWO ? false : undefined,
      },
      {
        to: `${basePath}/work/my-work-orders`,
        icon: <MyWorkOrdersIcon />,
        activeIcon: <MyWorkOrdersActiveIcon />,
        label: messages?.menus?.["cmms"]?.["SUB_MENUS"]?.[
          "MY_WORK_ORDERS"
        ] as string,
        notificationKey: "myTickets",
      },
      {
        to: `${basePath}/work/procedures`,
        icon: <ProcedureIcon />,
        activeIcon: <ProcedureIconActive />,
        label: messages?.menus?.["cmms"]?.["SUB_MENUS"]?.[
          "PROCEDURES"
        ] as string,
      },
      {
        to: `${basePath}/work/scheduler`,
        icon: <SchedulerIcon />,
        activeIcon: <SchedulerActiveIcon />,
        label: messages?.menus?.["cmms"]?.["SUB_MENUS"]?.[
          "SCHEDULING"
        ] as string,
      },
      {
        to: `${basePath}/work/planned-events`,
        icon: <PlannedEventsIcon />,
        activeIcon: <PlannedEventsActiveIcon />,
        label: messages?.menus?.["cmms"]?.["SUB_MENUS"]?.[
          "PLANNED_EVENTS"
        ] as string,
      },
      {
        to: `${basePath}/work/emails`,
        icon: <EmailIcon />,
        activeIcon: <EmailIconActive />,
        // Hidden entirely when the OEM's email feature is turned off (CRM).
        disableIf: (user: UserType) =>
          !user?.oem || !user.oem.paidFeatures?.includes(paidFeatures.emails),
        label: messages?.menus?.["cmms"]?.["SUB_MENUS"]?.["EMAILS"] as string,
      },
      {
        to: `${basePath}/work/insights`,
        icon: <InsightsIcon />,
        activeIcon: <InsightsIconActive />,
        label: messages?.menus?.["cmms"]?.["SUB_MENUS"]?.["INSIGHTS"] as string,
      },
      {
        to: `${basePath}/work/archived-work-orders`,
        icon: <ArchiveWorkOrderIcon />,
        activeIcon: <ArchiveWorkOrderIcon />,
        label: messages?.menus?.["cmms"]?.["SUB_MENUS"]?.[
          "ARCHIVED_WORK_ORDERS"
        ] as string,
        isActive: isArchivedWO ? true : undefined,
      },
    ],
    // Connections Routes
    [
      {
        to: companyBasePath,
        icon: <CompanyIcon />,
        activeIcon: <CompanyIconActive />,
        label: messages?.menus?.["asset-hub"]?.["SUB_MENUS"]?.[
          "COMPANIES"
        ] as string,
      },
      {
        to: `${basePath}/asset-hub/contacts`,
        icon: <ContactsIcon />,
        activeIcon: <ContactsIconActive />,
        label: messages?.menus?.["asset-hub"]?.["SUB_MENUS"]?.[
          "CONTACTS"
        ] as string,
      },
      {
        to: `${basePath}/customer-portal/my-portals`,
        icon: <PortalsIcon />,
        activeIcon: <PortalsActiveIcon />,
        label: messages?.menus?.["customerPortal"]?.["SUB_MENUS"]?.[
          "PORTALS"
        ] as string,
      },
      {
        to: `${basePath}/customer-portal/shared-products`,
        icon: <ProductsIcon />,
        activeIcon: <ProductsActiveIcon />,
        label: messages?.menus?.["customerPortal"]?.["SUB_MENUS"]?.[
          "SHARED_PRODUCTS"
        ] as string,
      },
      {
        to: `${basePath}/work/quotes`,
        icon: <QuotesInvoicesNavIcon />,
        activeIcon: <QuotesInvoicesNavIconActive />,
        disableIf: (user: UserType) =>
          !isPaidFeatureAvailable(
            paidFeatures.quotesAndInvoices,
            user,
            appConfig,
          ),
        label: messages?.menus?.["customerPortal"]?.["SUB_MENUS"]?.[
          "QUOTES"
        ] as string,
      },
      {
        to: `${basePath}/work/invoices`,
        icon: <InvoicesNavIcon />,
        activeIcon: <InvoicesNavIconActive />,
        disableIf: (user: UserType) =>
          !isPaidFeatureAvailable(
            paidFeatures.quotesAndInvoices,
            user,
            appConfig,
          ),
        label: messages?.menus?.["customerPortal"]?.["SUB_MENUS"]?.[
          "INVOICES"
        ] as string,
      },
    ],
    // AI Routes
    [
      {
        to: `${basePath}/ai/notetaker`,
        icon: <AiNotetakerIcon />,
        activeIcon: <AiNotetakerIconActive />,
        label: messages?.menus?.["industrial-ai"]?.["SUB_MENUS"]?.[
          "AI_NOTETAKER"
        ] as string,
      },
      {
        to: `${basePath}/ai/assistant`,
        icon: <AiAssistantsIcon />,
        activeIcon: <AiAssistantsIconActive />,
        label: messages?.menus?.["industrial-ai"]?.["SUB_MENUS"]?.[
          "AI_ASSISTANTS"
        ] as string,
      },
      {
        to: `${basePath}/ai/chat`,
        icon: <AiChatsIcon />,
        activeIcon: <AiChatsIconActive />,
        label: messages?.menus?.["industrial-ai"]?.["SUB_MENUS"]?.[
          "AI_CHATS"
        ] as string,
      },
      {
        to: `${basePath}/ai/search`,
        icon: <AISearchIcon />,
        activeIcon: <AISearchIconActive />,
        disableIf: (user: UserType) =>
          !user.oem ||
          !user.oem.installedProducts.find(
            (installedProduct) =>
              installedProduct.type === PRODUCT_TYPES.INDUSTRIAL_AI,
          ) ||
          !user.oem.paidFeatures.includes(paidFeatures.aiSearch),
        label: messages?.menus?.["industrial-ai"]?.["SUB_MENUS"]?.[
          "AI_SEARCH"
        ] as string,
      },
      {
        to: `${basePath}/ai/knowledge-base`,
        icon: <AIKnowledgeBaseIcon />,
        activeIcon: <AIKnowledgeBaseIconActive />,
        disableIf: (user: UserType) =>
          !user.oem ||
          !user.oem.installedProducts.find(
            (installedProduct) =>
              installedProduct.type === PRODUCT_TYPES.INDUSTRIAL_AI,
          ) ||
          !user.oem.paidFeatures.includes(paidFeatures.aiSearch),
        label: messages?.menus?.["industrial-ai"]?.["SUB_MENUS"]?.[
          "KNOWLEDGE_BASE"
        ] as string,
      },
    ],
  ];

  const footerRoutes: RouteConfig[] = [
    {
      to: `${basePath}/settings`,
      icon: <SettingsIcon />,
      activeIcon: <SettingsActiveIcon />,
      label: (messages?.["sidenav"]?.["settings"] as string) || "Settings",
    },
    ...(hasIntercomAppId
      ? [
          {
            icon: <HelpIcon />,
            activeIcon: <HelpActiveIcon />,
            label:
              (messages?.["sidenav"]?.["helpCenter"] as string) ||
              "Help Center",
            className: "help-link",
          },
        ]
      : []),
  ];

  // Modules the role can't open are left out of the menu.
  const withPermissions = (route: RouteConfig): RouteConfig =>
    route.to
      ? {
          ...route,
          disableIf: (user, appFeatures) =>
            isHiddenInNavigation(user, route.to!) ||
            !!route.disableIf?.(user, appFeatures),
        }
      : route;

  return {
    contentRouteSections: contentRouteSections.map((section) =>
      section.map(withPermissions),
    ),
    footerRoutes,
  };
}
